import React, { useState, useEffect } from 'react';
import { Plus, DollarSign, Calendar, FileText, Check, X, Edit2, Trash2, AlertCircle, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import axios from 'axios';
import { formatDateDisplay, normalizeDateForStorage, todayAsStorageDate } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const normalizeLoanDates = (loan) => ({
  ...loan,
  date: normalizeDateForStorage(loan?.date),
  paidDate: normalizeDateForStorage(loan?.paidDate)
});

const Loans = () => {
  const { isManager, currentUser, users } = useAuth();
  const { addNotification } = useNotification();
  const activeUsers = users.filter((user) => user.active !== false);

  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmar',
    confirmClassName: 'bg-red-600 hover:bg-red-700',
    action: null
  });

  // Pagination
  const [formData, setFormData] = useState({
    technicianId: '',
    technicianName: '',
    loanType: 'prestamo',
    amount: '',
    description: '',
    date: todayAsStorageDate(),
    status: 'pending',
    monthlyRecurring: false
  });

  // Fetch loans
  const fetchLoans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/loans`);
      let loansData = res.data;

      // Filter by user if not admin
      if (!isManager && currentUser) {
        loansData = loansData.filter(loan =>
          loan.technicianName === currentUser.name || loan.technicianId === currentUser.id
        );
      }

      setLoans((loansData || []).map(normalizeLoanDates));
    } catch (error) {
      console.error('Error fetching loans:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  const handleCreateLoan = async () => {
    if (!formData.amount || !formData.description) {
      addNotification('Campos requeridos', 'Por favor completa todos los campos requeridos.', 'warning');
      return;
    }

    let techName = formData.technicianName;
    let techId = formData.technicianId;

    if (formData.technicianId) {
      const user = users.find(u => u.id === formData.technicianId);
      if (user) {
        techName = user.name;
        techId = user.id;
      }
    }

    const loanData = {
      technicianName: techName,
      technicianId: techId,
      loanType: formData.loanType,
      amount: parseFloat(formData.amount),
      description: formData.description,
      date: normalizeDateForStorage(formData.date),
      status: 'pending',
      paidDate: '',
      monthlyRecurring: !!formData.monthlyRecurring,
      createdBy: currentUser?.name || 'Admin'
    };

    try {
      const res = await axios.post(`${API_URL}/api/loans`, loanData);
      setLoans(prev => [res.data, ...prev]);
      setIsCreateModalOpen(false);
      resetForm();
      addNotification('Préstamo Creado', `Se registró un préstamo de $${formData.amount} para ${techName}`, 'success');
    } catch (error) {
      console.error('Error creating loan:', error);
      addNotification('Error', 'Error al crear el préstamo', 'error');
    }
  };

  const handleUpdateLoan = async () => {
    if (!selectedLoan) return;

    const loanData = {
      ...selectedLoan,
      amount: parseFloat(formData.amount),
      description: formData.description,
      date: normalizeDateForStorage(formData.date),
      loanType: formData.loanType,
      status: formData.status,
      paidDate: formData.status === 'paid' ? todayAsStorageDate() : '',
      monthlyRecurring: !!formData.monthlyRecurring
    };

    try {
      const res = await axios.put(`${API_URL}/api/loans/${selectedLoan.id}`, loanData);
      setLoans(prev => prev.map(loan => loan.id === selectedLoan.id ? res.data : loan));
      setIsEditModalOpen(false);
      setSelectedLoan(null);
      resetForm();
      addNotification('Préstamo Actualizado', 'El préstamo se actualizó correctamente', 'success');
    } catch (error) {
      console.error('Error updating loan:', error);
      addNotification('Error', 'Error al actualizar el préstamo', 'error');
    }
  };

  const handleMarkAsPaid = async (loan) => {
    const loanData = {
      ...loan,
      status: 'paid',
      paidDate: todayAsStorageDate()
    };

    try {
      const res = await axios.put(`${API_URL}/api/loans/${loan.id}`, loanData);
      setLoans(prev => prev.map(l => l.id === loan.id ? res.data : l));
      addNotification('Préstamo Liquidado', `El préstamo de ${loan.technicianName} fue marcado como pagado`, 'success');
    } catch (error) {
      console.error('Error marking loan as paid:', error);
      addNotification('Error', 'Error al liquidar el préstamo', 'error');
    }
  };

  const closeConfirmDialog = () => {
    setConfirmState((prev) => ({
      ...prev,
      open: false,
      action: null
    }));
  };

  const runConfirmAction = async () => {
    if (!confirmState.action) return;

    try {
      await confirmState.action();
    } finally {
      closeConfirmDialog();
    }
  };

  const handleDeleteLoan = async (loanId) => {
    setConfirmState({
      open: true,
      title: 'Eliminar préstamo',
      message: 'Este préstamo se eliminará definitivamente.',
      confirmLabel: 'Eliminar',
      confirmClassName: 'bg-red-600 hover:bg-red-700',
      action: async () => {
        try {
          await axios.delete(`${API_URL}/api/loans/${loanId}`);
          setLoans(prev => prev.filter(loan => loan.id !== loanId));
          addNotification('Préstamo Eliminado', 'El préstamo se eliminó correctamente', 'info');
        } catch (error) {
          console.error('Error deleting loan:', error);
          addNotification('Error', 'Error al eliminar el préstamo', 'error');
        }
      }
    });
  };

  const openEditModal = (loan) => {
    setSelectedLoan(loan);
    setFormData({
      technicianId: loan.technicianId || '',
      technicianName: loan.technicianName,
      loanType: loan.loanType || 'prestamo',
      amount: loan.amount.toString(),
      description: loan.description,
      date: normalizeDateForStorage(loan.date),
      status: loan.status,
      monthlyRecurring: !!loan.monthlyRecurring
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      technicianId: '',
      technicianName: '',
      loanType: 'prestamo',
      amount: '',
      description: '',
      date: todayAsStorageDate(),
      status: 'pending',
      monthlyRecurring: false
    });
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getRecurringBadge = (loan) => {
    if (!loan?.monthlyRecurring) return null;
    return loan.recurrenceSource ? 'Base mensual' : 'Cuota mensual';
  };

  const getLoanTypeLabel = (loanType) =>
    (loanType || 'prestamo') === 'descuento' ? 'Descuento' : 'Préstamo';

  const renderLoanStatus = (loan) => (
    loan.status === 'pending' ? (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">
        <AlertCircle size={12} />
        Pendiente
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full">
        <Check size={12} />
        Pagado {loan.paidDate && `(${formatDateDisplay(loan.paidDate)})`}
      </span>
    )
  );

  const renderLoanActions = (loan, mobile = false) => {
    if (!isManager) return null;

    return (
      <div className={`flex items-center ${mobile ? 'justify-end gap-2' : 'justify-center gap-1'}`}>
        {loan.status === 'pending' && (
          <button
            onClick={() => handleMarkAsPaid(loan)}
            className={`rounded-xl text-emerald-600 hover:bg-emerald-50 transition-colors ${mobile ? 'p-2.5' : 'p-2'}`}
            title="Marcar como pagado"
          >
            <Check size={18} />
          </button>
        )}
        <button
          onClick={() => openEditModal(loan)}
          className={`rounded-xl text-blue-600 hover:bg-blue-50 transition-colors ${mobile ? 'p-2.5' : 'p-2'}`}
          title="Editar"
        >
          <Edit2 size={18} />
        </button>
        <button
          onClick={() => handleDeleteLoan(loan.id)}
          className={`rounded-xl text-red-500 hover:bg-red-50 transition-colors ${mobile ? 'p-2.5' : 'p-2'}`}
          title="Eliminar"
        >
          <Trash2 size={18} />
        </button>
      </div>
    );
  };

  const filteredLoans = loans.filter((loan) => {
    const matchesSearch =
      !searchTerm ||
      (loan.technicianName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (loan.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (getLoanTypeLabel(loan.loanType) || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (loan.date || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus ? loan.status === filterStatus : true;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const {
    currentPage,
    itemsPerPage,
    paginatedData: paginatedLoans,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange
  } = usePagination(filteredLoans, 25);

  // Calculate totals
  const pendingLoans = filteredLoans.filter(l => l.status === 'pending');
  const paidLoans = filteredLoans.filter(l => l.status === 'paid');
  const totalPending = pendingLoans.reduce((sum, loan) => sum + (loan.amount || 0), 0);
  const totalPaid = paidLoans.reduce((sum, loan) => sum + (loan.amount || 0), 0);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-shrink-0 flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800">{'Préstamos / Descuentos'}</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isManager ? 'Gestiona préstamos y adelantos a técnicos' : 'Consulta tus préstamos y descuentos'}
          </p>
        </div>
        {isManager && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/25 font-medium"
          >
            <Plus size={18} />
            <span>Nuevo</span>
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
      <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl border border-amber-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-amber-600 font-semibold text-sm">Pendientes</span>
            <div className="p-2 bg-amber-100 rounded-xl">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-amber-700">${formatCurrency(totalPending)}</p>
          <p className="text-xs text-amber-600 mt-1">{pendingLoans.length} {'préstamo'}{pendingLoans.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-white p-6 rounded-2xl border border-emerald-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-emerald-600 font-semibold text-sm">Liquidados</span>
            <div className="p-2 bg-emerald-100 rounded-xl">
              <Check size={20} className="text-emerald-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-emerald-700">${formatCurrency(totalPaid)}</p>
          <p className="text-xs text-emerald-600 mt-1">{paidLoans.length} {'préstamo'}{paidLoans.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-600 font-semibold text-sm">Total General</span>
            <div className="p-2 bg-slate-100 rounded-xl">
              <DollarSign size={20} className="text-slate-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-700">${formatCurrency(totalPending + totalPaid)}</p>
          <p className="text-xs text-slate-600 mt-1">{filteredLoans.length} {'préstamo'}{filteredLoans.length !== 1 ? 's' : ''} total</p>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por tecnico, descripcion o fecha..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="paid">Pagados</option>
          </select>
        </div>
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-600">
            <span className="font-semibold text-primary">{filteredLoans.length}</span> {'préstamo'}{filteredLoans.length !== 1 ? 's' : ''} encontrado{filteredLoans.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {loans.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}


      {/* Loans Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="space-y-3 p-3 lg:hidden">
          {paginatedLoans.map(loan => (
            <article key={loan.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{'TÃ©cnico'}</p>
                  <p className="truncate text-sm font-semibold text-slate-900">{loan.technicianName}</p>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  {formatDateDisplay(loan.date)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Tipo</p>
                  <p className="mt-1 text-slate-700">{getLoanTypeLabel(loan.loanType)}</p>
                  {getRecurringBadge(loan) && (
                    <span
                      className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        loan.recurrenceSource
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {getRecurringBadge(loan)}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Monto</p>
                  <p className="mt-1 font-semibold text-slate-900">${formatCurrency(loan.amount)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Estado</p>
                  <div className="mt-1">{renderLoanStatus(loan)}</div>
                </div>
                <div className="col-span-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{'Descripción'}</p>
                  <p className="mt-1 text-slate-700">{loan.description}</p>
                </div>
              </div>

              {isManager && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  {renderLoanActions(loan, true)}
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full text-left min-w-[800px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-4 font-semibold text-slate-600 text-sm">Fecha</th>
                <th className="px-4 py-4 font-semibold text-slate-600 text-sm">{'Técnico'}</th>
                <th className="px-4 py-4 font-semibold text-slate-600 text-sm">Tipo</th>
                <th className="px-4 py-4 font-semibold text-slate-600 text-sm">Monto</th>
                <th className="px-4 py-4 font-semibold text-slate-600 text-sm">{'Descripción'}</th>
                <th className="px-4 py-4 font-semibold text-slate-600 text-sm">Estado</th>
                {isManager && <th className="px-4 py-4 font-semibold text-slate-600 text-sm text-center">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedLoans.map(loan => (
                <tr key={loan.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-4 text-slate-600 text-sm">{formatDateDisplay(loan.date)}</td>
                  <td className="px-4 py-4 text-slate-800 font-medium text-sm">{loan.technicianName}</td>
                  <td className="px-4 py-4 text-slate-600 text-sm">
                    <div className="flex flex-col gap-2">
                      <span>{getLoanTypeLabel(loan.loanType)}</span>
                      {getRecurringBadge(loan) && (
                        <span
                          className={`inline-flex w-fit rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            loan.recurrenceSource
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {getRecurringBadge(loan)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-slate-800 font-semibold text-sm">${formatCurrency(loan.amount)}</td>
                  <td className="px-4 py-4 text-slate-600 text-sm max-w-xs truncate" title={loan.description}>
                    {loan.description}
                  </td>
                  <td className="px-4 py-4">
                    {renderLoanStatus(loan)}
                  </td>
                  {isManager && (
                    <td className="px-4 py-4">
                      {renderLoanActions(loan)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loans.length === 0 && (
          <div className="p-12 text-center">
            <DollarSign size={48} className="mx-auto mb-4 text-slate-200" />
            <p className="text-lg font-medium text-slate-600 mb-1">{'No hay préstamos registrados'}</p>
            <p className="text-sm text-slate-500">
              {isManager ? 'Crea un nuevo préstamo para comenzar' : 'No tienes préstamos registrados'}
            </p>
          </div>
        )}

      </div>

      </div>
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
            <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-xl">
                  <DollarSign size={20} />
                </div>
                <h3 className="font-heading font-bold text-slate-800">Nuevo</h3>
              </div>
              <button onClick={() => { setIsCreateModalOpen(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{'Técnico *'}</label>
                <select
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.technicianId}
                  onChange={(e) => setFormData({ ...formData, technicianId: e.target.value })}
                >
                  <option value="">{'-- Seleccionar técnico --'}</option>
                  {activeUsers.filter(u => u.role === 'collaborator').map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tipo *</label>
                  <select
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.loanType}
                    onChange={(e) => setFormData({ ...formData, loanType: e.target.value })}
                  >
                    <option value="prestamo">Préstamo</option>
                    <option value="descuento">Descuento</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Monto ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{'Descripción / Motivo *'}</label>
                <textarea
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                  placeholder="Ej: Adelanto de sueldo, Préstamo para gasolina, etc."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={formData.monthlyRecurring}
                  onChange={(e) => setFormData({ ...formData, monthlyRecurring: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span>Repetir mensualmente</span>
              </label>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => { setIsCreateModalOpen(false); resetForm(); }}
                className="px-6 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateLoan}
                className="px-8 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover shadow-lg shadow-primary/25 transition-all"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedLoan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
            <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                  <Edit2 size={20} />
                </div>
                <h3 className="font-heading font-bold text-slate-800">{'Editar Préstamo'}</h3>
              </div>
              <button onClick={() => { setIsEditModalOpen(false); setSelectedLoan(null); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{'Técnico'}</label>
                <input
                  type="text"
                  className="w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-600"
                  value={selectedLoan.technicianName}
                  disabled
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                  <select
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.loanType}
                    onChange={(e) => setFormData({ ...formData, loanType: e.target.value })}
                  >
                    <option value="prestamo">Préstamo</option>
                    <option value="descuento">Descuento</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Monto ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{'Descripción / Motivo'}</label>
                <textarea
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={formData.monthlyRecurring}
                  onChange={(e) => setFormData({ ...formData, monthlyRecurring: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span>Repetir mensualmente</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                <select
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagado</option>
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => { setIsEditModalOpen(false); setSelectedLoan(null); resetForm(); }}
                className="px-6 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateLoan}
                className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg transition-all"
              >
                Actualizar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        confirmClassName={confirmState.confirmClassName}
        onCancel={closeConfirmDialog}
        onConfirm={runConfirmAction}
      />
    </div>
  );
};

export default Loans;
